/**
 * Formação de preço (calculadora do produto e prévia de preços da compra).
 *
 * Regras portadas do Tirou (prc_sugerir_preco / arredondar_preco), em centavos:
 *  - encargos = imposto + taxa de cartão + comissão + outros (todos POR DENTRO do preço);
 *  - margem sobre a VENDA ('sale'): preço = custo / (1 - encargos - margem);
 *  - margem sobre o CUSTO ('cost'): preço = custo × (1 + margem) / (1 - encargos);
 *  - o arredondamento é a ÚLTIMA coisa (arredondar o custo antes faz a margem variar com o preço);
 *  - os modos ",90", ",99", "dez centavos" e "real acima" nunca descem abaixo do calculado:
 *    descer comeria a margem que a fórmula acabou de calcular;
 *  - divisor ≤ 1% (encargos + margem ≥ 99%) é margem inviável: sem sugestão, com motivo.
 *
 * Os percentuais viram inteiros (escala 1e6 = 100%) antes da divisão: assim a conta é a
 * divisão de dois inteiros, sem o ruído de ponto flutuante que faria 801 / 0,9 virar
 * 890,0000000001 e o modo ",90" saltar para o real seguinte.
 */
import type { PricingSettings } from './settings';

export type PriceRounding = PricingSettings['rounding'];

/** Rótulos dos modos de arredondamento (tela de configurações). */
export const PRICE_ROUNDING_LABELS: Record<PriceRounding, string> = {
  none: 'Sem arredondar (centavo exato)',
  ten_cents: 'Para cima, nos 10 centavos',
  x90: 'Terminar em ,90',
  x99: 'Terminar em ,99',
  whole: 'Real mais próximo',
  whole_up: 'Real acima',
};

/** Escala dos percentuais: 1_000_000 = 100%. */
const SCALE = 1_000_000;
/** Divisor mínimo aceito (1%): abaixo disso o preço explode e não significa nada. */
const MIN_DIVISOR = SCALE / 100;
/** Folga contra resíduo de ponto flutuante ao comparar com o preço calculado (centavos). */
const EPS = 1e-6;

function scaled(percent: number | null | undefined): number {
  const p = Number(percent ?? 0);
  return Number.isFinite(p) && p > 0 ? Math.round(p * (SCALE / 100)) : 0;
}

/** Soma dos encargos em % (imposto + cartão + comissão + outros). */
export function pricingChargesPercent(settings: Pick<PricingSettings, 'taxPercent' | 'cardFeePercent' | 'commissionPercent' | 'otherPercent'>): number {
  return round2((scaled(settings.taxPercent) + scaled(settings.cardFeePercent) + scaled(settings.commissionPercent) + scaled(settings.otherPercent)) / (SCALE / 100));
}

function chargesScaled(settings: PricingSettings): number {
  return scaled(settings.taxPercent) + scaled(settings.cardFeePercent) + scaled(settings.commissionPercent) + scaled(settings.otherPercent);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Termina o preço calculado (em centavos, com fração) conforme o modo da loja.
 * Zero e negativo não se arredondam para cima: não há preço a proteger.
 */
export function roundPrice(rawCents: number, mode: PriceRounding): number {
  if (!Number.isFinite(rawCents) || rawCents <= 0) return Math.max(0, Math.round(rawCents || 0));
  switch (mode) {
    case 'ten_cents':
      return Math.ceil(rawCents / 10 - EPS) * 10;
    case 'x90':
    case 'x99': {
      // O ,90 do próprio real quando ele NÃO fica abaixo do calculado; ficando, o do real seguinte.
      // 8,95 vira 9,90, jamais 8,90.
      const ending = mode === 'x90' ? 90 : 99;
      const candidate = Math.floor(rawCents / 100) * 100 + ending;
      return candidate + EPS >= rawCents ? candidate : candidate + 100;
    }
    case 'whole':
      return Math.round(rawCents / 100) * 100;
    case 'whole_up':
      return Math.ceil(rawCents / 100 - EPS) * 100;
    case 'none':
    default:
      return Math.round(rawCents);
  }
}

export type PriceSuggestionReason = 'no_cost' | 'impossible_margin';

export interface PriceSuggestion {
  /** Preço sugerido já arredondado (null quando não há sugestão possível). */
  priceCents: number | null;
  /** Resultado da fórmula antes do arredondamento (fração de centavo preservada). */
  rawPriceCents: number | null;
  costCents: number;
  /** Encargos somados (%) e margem alvo (%) usados na conta. */
  chargesPercent: number;
  marginPercent: number;
  marginMode: PricingSettings['marginMode'];
  /** Parcelas do preço arredondado: encargos (por dentro) e lucro que sobra. */
  chargesCents: number | null;
  profitCents: number | null;
  /** Preço / custo (ex.: 2,5 = 2,5×). */
  markup: number | null;
  reason: PriceSuggestionReason | null;
  message: string | null;
}

/** Preço sugerido para um custo, pelos parâmetros de precificação da loja. */
export function suggestPrice(costCents: number | null | undefined, settings: PricingSettings): PriceSuggestion {
  const cost = Math.max(0, Math.round(Number(costCents ?? 0)));
  const charges = chargesScaled(settings);
  const margin = scaled(settings.targetMarginPercent);
  const base: Omit<PriceSuggestion, 'priceCents' | 'rawPriceCents' | 'chargesCents' | 'profitCents' | 'markup' | 'reason' | 'message'> = {
    costCents: cost,
    chargesPercent: round2(charges / (SCALE / 100)),
    marginPercent: round2(margin / (SCALE / 100)),
    marginMode: settings.marginMode,
  };
  const empty = { priceCents: null, rawPriceCents: null, chargesCents: null, profitCents: null, markup: null };
  if (cost <= 0) {
    return { ...base, ...empty, reason: 'no_cost', message: 'Informe o custo para calcular o preço sugerido.' };
  }
  const divisor = settings.marginMode === 'cost' ? SCALE - charges : SCALE - charges - margin;
  if (divisor <= MIN_DIVISOR) {
    return {
      ...base,
      ...empty,
      reason: 'impossible_margin',
      message:
        settings.marginMode === 'cost'
          ? 'Os encargos passam de 99% da venda. Revise imposto, taxas e comissão.'
          : 'Encargos e margem somam 99% ou mais da venda. Diminua a margem ou os encargos.',
    };
  }
  const numerator = settings.marginMode === 'cost' ? cost * (SCALE + margin) : cost * SCALE;
  const raw = numerator / divisor;
  const price = roundPrice(raw, settings.rounding);
  const chargesCents = Math.round((price * charges) / SCALE);
  return {
    ...base,
    priceCents: price,
    rawPriceCents: Math.round(raw * 1e4) / 1e4,
    chargesCents,
    profitCents: price - cost - chargesCents,
    markup: round2(price / cost),
    reason: null,
    message: null,
  };
}

export interface PriceBreakdown {
  priceCents: number;
  costCents: number;
  /** Encargos por dentro do preço (imposto, cartão, comissão, outros). */
  chargesCents: number;
  /** O que sobra depois do custo e dos encargos. */
  profitCents: number;
  /** Lucro / preço (%). */
  marginOnSalePercent: number | null;
  /** Lucro / custo (%). */
  marginOnCostPercent: number | null;
  markup: number | null;
}

/** Decompõe um preço em custo, encargos e lucro. */
export function priceBreakdown(priceCents: number, costCents: number | null | undefined, settings: PricingSettings): PriceBreakdown {
  const price = Math.round(priceCents);
  const cost = Math.max(0, Math.round(Number(costCents ?? 0)));
  const chargesCents = Math.round((price * chargesScaled(settings)) / SCALE);
  const profitCents = price - cost - chargesCents;
  return {
    priceCents: price,
    costCents: cost,
    chargesCents,
    profitCents,
    marginOnSalePercent: price > 0 ? round2((profitCents / price) * 100) : null,
    marginOnCostPercent: cost > 0 ? round2((profitCents / cost) * 100) : null,
    markup: cost > 0 ? round2(price / cost) : null,
  };
}

/**
 * Margem real (%) que um preço deixa depois do custo e dos encargos, na MESMA base da
 * margem alvo da loja: sobre a venda (lucro / preço) ou sobre o custo (lucro / custo).
 * Null quando a base é zero (sem preço ou sem custo).
 */
export function marginFor(priceCents: number, costCents: number | null | undefined, settings: PricingSettings): number | null {
  if (costCents == null) return null;
  const b = priceBreakdown(priceCents, costCents, settings);
  return settings.marginMode === 'cost' ? b.marginOnCostPercent : b.marginOnSalePercent;
}

/** Preço / custo (ex.: 2,5 = 2,5×). Null sem custo. */
export function priceMarkup(priceCents: number, costCents: number | null | undefined): number | null {
  if (!costCents || costCents <= 0) return null;
  return round2(priceCents / costCents);
}
